/**
 * Confidence indicator badge for document classification.
 *
 * Shows visual indicator of classification confidence level:
 * - High: Green
 * - Medium: Yellow
 * - Low: Red with warning icon
 */

import { AlertTriangle, CheckCircle2, HelpCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type ConfidenceLevel = "high" | "medium" | "low" | null;

interface ConfidenceBadgeProps {
  /** Confidence level */
  confidence: ConfidenceLevel;
  /** Optional reasoning text to show in tooltip */
  reasoning?: string | null;
  /** Show as inline text instead of badge */
  inline?: boolean;
  /** Optional className */
  className?: string;
}

const confidenceConfig = {
  high: {
    label: "High Confidence",
    shortLabel: "High",
    variant: "default" as const,
    icon: CheckCircle2,
    color: "text-green-600",
    bgColor: "bg-green-50 dark:bg-green-950",
    borderColor: "border-green-200 dark:border-green-800",
  },
  medium: {
    label: "Medium Confidence",
    shortLabel: "Medium",
    variant: "secondary" as const,
    icon: HelpCircle,
    color: "text-yellow-600",
    bgColor: "bg-yellow-50 dark:bg-yellow-950",
    borderColor: "border-yellow-200 dark:border-yellow-800",
  },
  low: {
    label: "Low Confidence",
    shortLabel: "Low",
    variant: "destructive" as const,
    icon: AlertTriangle,
    color: "text-red-600",
    bgColor: "bg-red-50 dark:bg-red-950",
    borderColor: "border-red-200 dark:border-red-800",
  },
};

export function ConfidenceBadge({
  confidence,
  reasoning,
  inline = false,
  className,
}: ConfidenceBadgeProps) {
  if (!confidence) {
    return null;
  }

  const config = confidenceConfig[confidence];
  const Icon = config.icon;

  const content = inline ? (
    <span className={cn("inline-flex items-center gap-1", config.color, className)}>
      <Icon className="h-4 w-4" />
      <span>{config.shortLabel}</span>
    </span>
  ) : (
    <Badge
      variant={config.variant}
      className={cn(
        "inline-flex items-center gap-1",
        confidence === "low" && "animate-pulse",
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );

  if (reasoning) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help">{content}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-xs font-medium mb-1">Classification Reasoning:</p>
          <p className="text-xs text-muted-foreground">{reasoning}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
}

/**
 * Warning banner for low confidence documents
 */
interface LowConfidenceWarningProps {
  /** Confidence level */
  confidence: ConfidenceLevel;
  /** Optional reasoning text */
  reasoning?: string | null;
  /** Optional className */
  className?: string;
}

export function LowConfidenceWarning({
  confidence,
  reasoning,
  className,
}: LowConfidenceWarningProps) {
  if (confidence !== "low" && confidence !== "medium") {
    return null;
  }

  const isLow = confidence === "low";

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg border",
        isLow
          ? "bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800"
          : "bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800",
        className
      )}
    >
      <AlertTriangle
        className={cn(
          "h-5 w-5 flex-shrink-0 mt-0.5",
          isLow ? "text-red-600" : "text-yellow-600"
        )}
      />
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            "text-sm font-medium",
            isLow ? "text-red-800 dark:text-red-200" : "text-yellow-800 dark:text-yellow-200"
          )}
        >
          {isLow ? "Low confidence classification" : "Medium confidence classification"}
        </p>
        <p
          className={cn(
            "text-sm mt-0.5",
            isLow ? "text-red-700 dark:text-red-300" : "text-yellow-700 dark:text-yellow-300"
          )}
        >
          Please verify the document type and extracted data carefully.
        </p>
        {reasoning && (
          <p
            className={cn(
              "text-xs mt-2 italic",
              isLow ? "text-red-600 dark:text-red-400" : "text-yellow-600 dark:text-yellow-400"
            )}
          >
            Reason: {reasoning}
          </p>
        )}
      </div>
    </div>
  );
}

export default ConfidenceBadge;
